import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { log } from "../lib/logger";
import { useAuth } from "./AuthContext";

const CartCtx = createContext({
  items: [],
  count: 0,
  totalAed: 0,
  loading: false,
  add: async () => {},
  remove: async () => {},
  refresh: async () => {},
});

/**
 * Holds the current user's cart (fixed-price items awaiting checkout).
 * Fetched once on login and after every mutation so the Header basket badge
 * always shows an accurate count.
 */
export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [totalAed, setTotalAed] = useState(0);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setItems([]); setTotalAed(0); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/me/cart");
      setItems(data.items || []);
      setTotalAed(data.total_aed || 0);
    } catch (err) {
      log.warn("[Cart] load failed:", err);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const add = useCallback(async (product_id, options = {}) => {
    if (!user) throw new Error("Sign in required");
    await api.post("/cart", { product_id, ...options });
    await refresh();
  }, [user, refresh]);

  const remove = useCallback(async (product_id) => {
    await api.delete(`/cart/${product_id}`);
    await refresh();
  }, [refresh]);

  return (
    <CartCtx.Provider value={{ items, count: items.length, totalAed, loading, add, remove, refresh }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
